import { FunctionComponent } from 'react';
import { BodyText, Gutter, List, ModalDialog } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { calculateSalaryDeductionEstimate, getFormattedPrice, getPriceIncludingVat } from '@cdaas/reusable-components-daas';
import {
  UserResponse,
  UserPoliciesResponseSingleDevicePolicyEnum,
  OrganizationResponse,
  AllowanceType,
} from '@cdaas/api-clients/dist/asset-admin-openapi';
import { BundlePrice, BundlePriceTypeEnum, LocalBundleEnvelope } from '@cdaas/api-clients/dist/product-service-openapi';
import { isSelectedBundleAvailable } from '../OrderUtils';

export interface ConfirmModalProps {
  user: UserResponse | null;
  organization: OrganizationResponse | null;
  chosenMainItemVariantId: number | undefined;
  bundleEnvelope: LocalBundleEnvelope | null;
  isOpen: boolean;
  handleCancel: () => void;
  handleConfirm: () => void;
}

const ConfirmModal: FunctionComponent<ConfirmModalProps> = ({
  user,
  organization,
  chosenMainItemVariantId,
  bundleEnvelope,
  isOpen,
  handleCancel,
  handleConfirm,
}) => {
  const { t } = useTranslation('orderFlow');

  const getPrice = (): BundlePrice | null => bundleEnvelope?.bundle.prices?.[0] ?? null;

  const renderPrice = () => {
    const price = getPrice();
    const total = price?.amount ? getFormattedPrice(getPriceIncludingVat(price.amount, price.vat_percentage), organization?.locale.language) : '';
    const currency = organization?.locale.currency.toUpperCase() || '';
    const priceToShow = `${total} ${currency}`;
    if (price?.type === BundlePriceTypeEnum.Once) {
      return t('bundle.confirmModal.oneTimePrice', { priceToShow });
    } else {
      return t('bundle.confirmModal.monthlyPrice', { priceToShow });
    }
  };

  const isLease = (): boolean => getPrice()?.type !== BundlePriceTypeEnum.Once;

  const renderLeasePeriod = () => {
    switch (getPrice()?.type) {
      case BundlePriceTypeEnum.Monthly12:
        return t('bundle.confirmModal.leasePeriod', { periodInMonths: 12 });
      case BundlePriceTypeEnum.Monthly24:
        return t('bundle.confirmModal.leasePeriod', { periodInMonths: 24 });
      case BundlePriceTypeEnum.Monthly30:
        return t('bundle.confirmModal.leasePeriod', { periodInMonths: 30 });
      case BundlePriceTypeEnum.Monthly36:
        return t('bundle.confirmModal.leasePeriod', { periodInMonths: 36 });
      case BundlePriceTypeEnum.Monthly48:
        return t('bundle.confirmModal.leasePeriod', { periodInMonths: 48 });
      case BundlePriceTypeEnum.Once:
      default:
        return '';
    }
  };

  const salaryDeductionEstimate = (allowanceType: AllowanceType, allowance: number, price: BundlePrice): number => {
    if ((allowanceType === AllowanceType.Monthly || allowanceType === AllowanceType.OneTime) && price.type !== BundlePriceTypeEnum.Once) {
      const months = allowanceType === AllowanceType.OneTime ? parseInt(price.type.split('_')[1]) || 1 : 1;
      return calculateSalaryDeductionEstimate(
        allowanceType === AllowanceType.OneTime ? allowance : allowance * months,
        allowanceType,
        getPriceIncludingVat(price.amount, price.vat_percentage) * months,
      );
    }
    return 0;
  };

  const priceExceedsAllowance = () => {
    const allowance = user?.allowance;
    const allowanceType = user?.allowance_type;
    const price = getPrice();
    if (allowanceType && allowance && price) {
      const estimatedSalaryDeduction = salaryDeductionEstimate(allowanceType, allowance, price);
      if (estimatedSalaryDeduction > 0) {
        return true;
      }
    }
    return false;
  };

  // Should only be called if monthly or one time allowance is set for the user and the actual price of the product exceeds it
  const renderExceededAllowance = () => {
    const allowanceType = user?.allowance_type;
    const allowance = user?.allowance;
    const price = getPrice();
    const priceType = price?.type || BundlePriceTypeEnum.Once;
    if (
      price &&
      (allowanceType === AllowanceType.Monthly || allowanceType === AllowanceType.OneTime) &&
      priceType !== BundlePriceTypeEnum.Once &&
      allowance
    ) {
      const estimatedSalaryDeduction = salaryDeductionEstimate(allowanceType, allowance, price);
      if (estimatedSalaryDeduction === 0) {
        return '';
      }
      const amountExceeded = `${getFormattedPrice(estimatedSalaryDeduction, organization?.locale.language)} ${
        organization?.locale.currency.toUpperCase() || ''
      }`;
      return allowanceType === AllowanceType.Monthly
        ? t('bundle.confirmModal.monthlySalaryDeductionExplanation', { amountExceeded })
        : t('bundle.confirmModal.oneTimeSalaryDeductionExplanation', { amountExceeded });
    }
    return '';
  };

  const singleDevicePolicyEnabled = () => {
    return user?.policies.single_device_policy === UserPoliciesResponseSingleDevicePolicyEnum.Enabled;
  };

  // User should not be allowed to even start the oob flow if single device policy is enabled, and they already have a device
  const renderSingleDevicePolicyToBeFull = () => {
    return t('bundle.confirmModal.singleDevicePolicyToBeFull');
  };

  const renderOutOfStock = () => {
    return t('bundle.confirmModal.outOfStock');
  };

  return (
    <ModalDialog
      isOpen={isOpen}
      size="lg"
      title={t('bundle.confirmModal.title')}
      acceptButtonText={t('bundle.confirmModal.confirmButton')}
      closeButtonText={t('bundle.confirmModal.cancelButton')}
      onCloseClick={handleCancel}
      onAcceptClick={handleConfirm}
    >
      <BodyText>{t('bundle.confirmModal.info')}</BodyText>
      <Gutter size="sm" />
      <List disc={true}>
        <List.Item>{renderPrice()}</List.Item>
        {isLease() && <List.Item>{renderLeasePeriod()}</List.Item>}
        {priceExceedsAllowance() && <List.Item>{renderExceededAllowance()}</List.Item>}
        {singleDevicePolicyEnabled() && <List.Item>{renderSingleDevicePolicyToBeFull()}</List.Item>}
        {!isSelectedBundleAvailable(bundleEnvelope, chosenMainItemVariantId) && <List.Item>{renderOutOfStock()}</List.Item>}
      </List>
    </ModalDialog>
  );
};

export default ConfirmModal;
